import { PERIOD_TOTAL } from '../../pages/dashboard/balance-sheet/types';
import { EnvironmentService } from '../environment/environment-service';
import { LanguageStore } from '../localization/language-store';
import { formatUserBasedOnDisplayNameAndLocale } from '../utilFunctions/pure-utils';
import { HasDependencies, hashString } from '../v2/api/types';
import { UserRolePreferencesSettings } from '../v2/api/userrolepreferences/types';
import { Filter } from '../v2/common/components/filter/filterbar/types';

export enum IntercomArticleKeys {
  FILTER_TRAY = 'FILTER_TRAY',
  DATA_VIEW = 'DATA_VIEW',
  BENCHMARK = 'BENCHMARK',
  FORECAST = 'FORECAST',
  SEGMENT = 'SEGMENT',
  EXPORT = 'EXPORT',
}

export const INTERCOM_CUSTOM_LAUNCHER_ID = 'appbar-intercom-button';
export class IntercomStore implements HasDependencies {
  private defaultIntercomArticleid = 5971932;

  private intercomKeyToArticleIdMap = {
    [PERIOD_TOTAL]: 6826931,
    [IntercomArticleKeys.FILTER_TRAY]: 5685680,
    [IntercomArticleKeys.DATA_VIEW]: 5685717,
    [IntercomArticleKeys.BENCHMARK]: 6169046,
    [IntercomArticleKeys.FORECAST]: 6961743,
    [IntercomArticleKeys.SEGMENT]: 6147673,
    [IntercomArticleKeys.EXPORT]: 8026031,
  };

  private panalytIntercomKey = 'qcxorkyv';

  constructor(
    readonly languageStore: LanguageStore,
    readonly environmentService: EnvironmentService
  ) {}

  public getDeps = (): (string | undefined)[] => [
    hashString([
      ...this.languageStore.getDeps(),
      ...this.environmentService.getDeps(),
    ]),
  ];

  public getCorrespondingArticleIdFromIntercomKey = (
    intercomKey: string
  ): number => {
    return (
      this.intercomKeyToArticleIdMap[
        intercomKey as keyof typeof this.intercomKeyToArticleIdMap
      ] ?? this.defaultIntercomArticleid
    );
  };

  public showArticleInIntercom = (intercomKey: string) => {
    window.Intercom(
      'showArticle',
      this.getCorrespondingArticleIdFromIntercomKey(intercomKey)
    );
  };

  public applyWindowIntercomSettings = (
    userRolePreferences: UserRolePreferencesSettings<Filter> | null,
    email: string | null
  ) => {
    if (window.intercomSettings && email) {
      const { selectedLanguage } = this.languageStore;
      const environmentName = this.environmentService.getEnvironment().name;
      const profile = userRolePreferences?.settings?.profile;

      const userId = `${email} - ${environmentName}`;
      this.updateSettings({
        name: profile
          ? formatUserBasedOnDisplayNameAndLocale(profile, selectedLanguage.id)
          : '',
        email,
        user_id: userId,
      });
    } else {
      const redactedEmail =
        email?.replace(/(^..).+(@.*$)/, '$1***$2') ?? '(none)';
      console.debug(
        `Couldn't apply Intercom settings (hasIntercomSettings, hasUserRolePrefs, email)`,
        Boolean(window.intercomSettings),
        Boolean(userRolePreferences),
        redactedEmail
      );
    }
  };

  public updateSettings = (settings: Intercom_.IntercomSettings): void => {
    window.intercomSettings = {
      ...window.intercomSettings,
      ...settings,
    };
    if (window.Intercom) {
      void window.Intercom('update', window.intercomSettings);
    } else {
      // @ts-ignore
      console.warn(
        `Couldn't update Intercom settings as Intercom wasn't initialized`
      );
    }
  };
}
